import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { LoggingService } from '../logging/logging.service';

/**
 * Service to help debug cookie issues across different domains
 */
@Injectable({ providedIn: 'root' })
export class CookieDebugService {
  constructor(
    private cookies: CookieService,
    private log: LoggingService
  ) {}

  /**
   * Debug all cookies and their accessibility
   */
  debugCookies(): void {
    this.log.info('CookieDebugService', '=== Cookie Debug Info ===');
    this.log.info('CookieDebugService', `Current domain: ${window.location.hostname}`);
    this.log.info('CookieDebugService', `Current origin: ${window.location.origin}`);
    
    // Get all cookies via document.cookie
    const rawCookies = document.cookie;
    this.log.info('CookieDebugService', `Raw document.cookie: ${rawCookies}`);
    
    // Parse cookies
    const parsedCookies = this.parseCookies(rawCookies);
    this.log.info('CookieDebugService', 'Parsed cookies:', parsedCookies);
    
    // Test specific auth cookies
    const authCookies = ['token', 'ws_token', 'access_token', 'refresh_token', 'email', 'givenName', 'profilePictureUrl'];
    this.log.info('CookieDebugService', 'Testing auth cookie access:');
    
    authCookies.forEach(name => {
      // Test with ngx-cookie-service
      const ngxValue = this.cookies.get(name);
      
      this.log.info('CookieDebugService', `Cookie '${name}':`);
      this.log.info('CookieDebugService', `  - ngx value: ${this.truncateValue(ngxValue)}`);
      this.log.info('CookieDebugService', `  - parsed: ${this.truncateValue(parsedCookies[name])}`);
    });
    
    // Check if we're on a subdomain
    if (window.location.hostname.includes('.finderwise.com')) {
      this.log.info('CookieDebugService', 'Running on finderwise.com subdomain - cookies should have domain=.finderwise.com');
    }
  }
  
  /**
   * Test token exchange endpoint
   */
  async testTokenExchange(token: string): Promise<void> {
    this.log.info('CookieDebugService', 'Testing token exchange...');
    
    try {
      const response = await fetch('/api/auth/exchange-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token }),
        credentials: 'include'
      });
      
      this.log.info('CookieDebugService', `Exchange response status: ${response.status}`);
      
      // Check Set-Cookie headers
      const setCookieHeaders = response.headers.get('set-cookie');
      if (setCookieHeaders) {
        this.log.info('CookieDebugService', `Set-Cookie headers: ${setCookieHeaders}`);
      }
      
      const result = await response.json();
      this.log.info('CookieDebugService', 'Exchange result:', result);
      
      // Wait a bit then check cookies again
      setTimeout(() => {
        this.log.info('CookieDebugService', 'Cookies after exchange:');
        this.debugCookies();
      }, 1000);
      
    } catch (error) {
      this.log.error('CookieDebugService', 'Token exchange failed:', error);
    }
  }
  
  private parseCookies(cookieString: string): Record<string, string> {
    if (!cookieString) return {};
    
    return cookieString.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      if (key) acc[key] = value || '';
      return acc;
    }, {} as Record<string, string>);
  }
  
  private truncateValue(value: string | undefined): string {
    if (!value) return 'null';
    if (value === 'secure' || value === 'httponly') return `[${value}]`;
    if (value.length > 50) return value.substring(0, 50) + '...';
    return value;
  }
}